/** Sentinel: do not write a value (e.g. divide by zero with skip). */
export const COMPUTED_SKIP = Symbol('computed-skip');

/**
 * @param {number | number[] | typeof COMPUTED_SKIP | undefined} value
 * @returns {number | typeof COMPUTED_SKIP | undefined}
 */
function toScalar(value) {
  if (value === COMPUTED_SKIP) return COMPUTED_SKIP;
  if (value === undefined) return undefined;
  if (Array.isArray(value)) {
    return value.reduce((sum, item) => sum + (Number.isFinite(item) ? item : 0), 0);
  }
  if (typeof value === 'number') return value;
  return undefined;
}

/**
 * @typedef {object} EvaluateExprContext
 * @property {(apiId: string) => number | undefined} getFieldNumericValue
 * @property {(apiId: string) => string | undefined} getFieldRawValue
 * @property {(tag: string, filter: ExprTagRefFilter | null | undefined) => number[]} getTagValues
 * @property {'skip' | 'zero'} [onDivideByZero]
 */

/**
 * Parse a numeric value from form/cell string (supports comma decimal separator).
 * @param {unknown} raw
 * @returns {number | undefined}
 */
export function parseNumericValue(raw) {
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  const normalized = String(raw).trim().replace(',', '.');
  if (normalized === '') return undefined;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * @param {string} timeStr - "HH:MM"
 * @returns {number | undefined} minutes since midnight
 */
function parseTimeToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return undefined;
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(timeStr.trim());
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return undefined;
  return hours * 60 + minutes;
}

/**
 * @param {number} startMinutes
 * @param {number} endMinutes
 * @param {'hours' | 'minutes'} unit
 * @returns {number}
 */
function computeDuration(startMinutes, endMinutes, unit) {
  let diff = endMinutes - startMinutes;
  if (diff < 0) diff += 24 * 60;
  return unit === 'minutes' ? diff : diff / 60;
}

/**
 * @param {number} value
 * @param {ComputedValueRound | null | undefined} round
 * @returns {number}
 */
export function applyRound(value, round) {
  if (!round || round.digits == null) return value;
  const factor = 10 ** round.digits;
  return Math.round(value * factor) / factor;
}

/**
 * @param {Expr} expr
 * @param {EvaluateExprContext} ctx
 * @returns {number | number[] | typeof COMPUTED_SKIP | undefined}
 */
export function evaluateExpr(expr, ctx) {
  if (!expr) return undefined;

  if (expr.constant != null) {
    return expr.constant;
  }

  if (expr.fieldRef) {
    return ctx.getFieldNumericValue(expr.fieldRef);
  }

  if (expr.tagRef) {
    return ctx.getTagValues(expr.tagRef.tag, expr.tagRef.filter);
  }

  if (expr.operation) {
    const { op, args, unit } = expr.operation;
    const evaluatedArgs = (args ?? []).map((arg) => evaluateExpr(arg, ctx));

    if (op === 'sum') {
      let total = 0;
      evaluatedArgs.forEach((arg) => {
        if (arg === COMPUTED_SKIP || arg === undefined) return;
        if (Array.isArray(arg)) {
          total += arg.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);
        } else if (Number.isFinite(arg)) {
          total += arg;
        }
      });
      return total;
    }

    if (op === 'duration') {
      const startArg = args?.[0];
      const endArg = args?.[1];
      if (!startArg || !endArg) return undefined;
      const startStr = startArg.fieldRef
        ? ctx.getFieldRawValue(startArg.fieldRef)
        : String(evaluatedArgs[0] ?? '');
      const endStr = endArg.fieldRef
        ? ctx.getFieldRawValue(endArg.fieldRef)
        : String(evaluatedArgs[1] ?? '');
      const startMin = parseTimeToMinutes(startStr);
      const endMin = parseTimeToMinutes(endStr);
      if (startMin === undefined || endMin === undefined) return undefined;
      const durationUnit = unit === 'minutes' ? 'minutes' : 'hours';
      return computeDuration(startMin, endMin, durationUnit);
    }

    if (evaluatedArgs.length < 2) return undefined;
    const left = toScalar(evaluatedArgs[0]);
    const right = toScalar(evaluatedArgs[1]);
    if (left === COMPUTED_SKIP || right === COMPUTED_SKIP) return COMPUTED_SKIP;
    if (left === undefined || right === undefined) return undefined;

    switch (op) {
      case 'add':
        return left + right;
      case 'sub':
        return left - right;
      case 'mul':
        return left * right;
      case 'div': {
        if (right === 0) {
          return ctx.onDivideByZero === 'zero' ? 0 : COMPUTED_SKIP;
        }
        return left / right;
      }
      default:
        return undefined;
    }
  }

  return undefined;
}

/**
 * @param {ComputedValueRule[]} rules
 * @param {(conditions: FormItemCondition[]) => boolean} matchWhen
 * @returns {ComputedValueRule | null}
 */
export function selectRule(rules, matchWhen) {
  if (!rules?.length) return null;
  return rules.find((rule) => matchWhen(rule.when ?? [])) ?? null;
}

/**
 * @param {number} value
 * @param {FormItem | null | undefined} field
 * @returns {string}
 */
export function formatComputedFieldValue(value, field) {
  const type = field?.text?.type;
  if (type === 'int') return String(Math.round(value));
  return String(value);
}

/**
 * @param {number} value
 * @param {string} [cellType]
 * @returns {string}
 */
export function formatComputedCellValue(value, cellType) {
  if (cellType === 'number' || cellType === 'money') {
    return String(value);
  }
  return String(value);
}
